<script setup lang="ts">
import dayjs from "dayjs";
import QuotaIndicator from "@/components/QuotaIndicator.vue";
import { Dialog } from "@/shadcn/components/ui/dialog";
import { DialogHeader, DialogScrollContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import ActivityVariantEdit from "@/components/activity/ActivityVariantEdit.vue";
import { Button } from "@/shadcn/components/ui/button";
import { Settings2Icon } from "@lucide/vue";
import { displayDate } from "@/utils/date";

const props = defineProps({
  activityOptions: {
    type: Array,
    required: true
  },
  variants: {
    type: Array,
    required: true
  },
  updateQuotaUrl: {
    type: String,
    required: true
  },
  timezone: {
    type: String,
    required: true
  },
  stages: {
    type: Array,
    default: () => []
  },
})

const csrf_token = window.Laravel.csrfToken;

</script>

<template>
  <form :action="updateQuotaUrl" method="POST">
    <input type="hidden" name="_method" value="POST">
    <input type="hidden" name="_token" :value="csrf_token">
    <div class="table-responsive">
      <table class="table table-sm table-striped">
        <tr>
          <th class="tw:text-left">{{ $t('Options') }}</th>
          <th class="tw:text-left">{{ $t('Quota') }}</th>
          <th class="tw:text-left">{{ $t('Sale period') }}</th>
          <th class="tw:text-left">{{ $t('Associated stages') }}</th>
          <th class="tw:text-left">{{ $t('Actions') }}</th>
        </tr>
        <template v-for="variant in variants" :key="variant.id">
          <tr>
            <td>
              <template v-for="activityOption in props.activityOptions">
                <template v-if="variant.option_values.find(activityOptionValue => activityOptionValue.activity_option_id === activityOption.id)">
                  <span class="badge badge-light tw:inline-block">
                    {{ activityOption.name ?? activityOption.code }}:
                    {{ variant.option_values.find(activityOptionValue => activityOptionValue.activity_option_id === activityOption.id).value ?? variant.option_values.find(activityOptionValue => activityOptionValue.activity_option_id === activityOption.id).code }}
                  </span>
                  <br>
                </template>
              </template>
            </td>
            <td>
              <QuotaIndicator :quota="variant.quota" />
              <input type="number"
                     :name="'quota[' + variant.id + ']'"
                     :value="variant.quota.total"
                     class="form-control form-control-sm tw:w-[70px]"
                     :placeholder="$t('Total quota')"
                     min="0"
                     step="1" />
            </td>
            <td>
              <small class="text-nowrap d-block" v-if="variant.sale_start_at">
                {{ $t("From: {date}", { date: displayDate(dayjs.utc(variant.sale_start_at).tz(timezone)) }) }}
              </small>
              <small class="text-nowrap d-block" v-if="variant.sale_end_at">
                {{ $t("To: {date}", { date: displayDate(dayjs.utc(variant.sale_end_at).tz(timezone)) }) }}
              </small>
            </td>
            <td>
              <template v-for="stage in variant.stages">
                <span class="badge badge-light tw:inline-block">
                  {{ stage.name }}
                </span>
                <br>
              </template>
            </td>
            <td class="tw:align-middle">
              <Dialog v-cloak>
                <DialogTrigger as-child>
                  <Button variant="secondary" size="sm" class="tw:mb-2 tw:mr-2">
                    <Settings2Icon class="tw:w-3 tw:h-3" /> {{ $t('Edit') }}
                  </Button>
                </DialogTrigger>
                <DialogScrollContent size="md">
                  <DialogHeader>
                    <DialogTitle>
                      {{ $t('Variant Edit') }}
                    </DialogTitle>
                  </DialogHeader>
                  <ActivityVariantEdit
                    :model-value="variant"
                    :activity-options="activityOptions"
                    :stages="stages"
                    :timezone="timezone"
                  />
                </DialogScrollContent>
              </Dialog>
            </td>
          </tr>
        </template>
      </table>
    </div>

    <div class="tw:sticky tw:bottom-0 tw:p-4 tw:bg-zinc-50/30 tw:backdrop-blur-xl">
      <Button type="submit">
        {{ $t("Save all quotas") }}
      </Button>
    </div>

  </form>
</template>

<style scoped>

</style>
